import { Box, Divider, Grid, Paper, Typography } from '@mui/material';
import React from 'react';

import {
  formatDate,
  formatMoney,
} from '../../../../../../../../../../../../../lib/functions/utils';

function calculateIRR(
  cuota: number,
  numCuotas: number,
  montoPrestamo: number,
): number {
  const maxIterations = 1000; // Máximo de iteraciones
  const tolerance = 1e-9; // Tolerancia para convergencia
  let tasa = 0.01; // Suposición inicial para la tasa de interés (1%)

  for (let i = 0; i < maxIterations; i++) {
    let f = 0; // Función f(tasa)
    let df = 0; // Derivada f'(tasa)

    for (let n = 1; n <= numCuotas; n++) {
      f += cuota / Math.pow(1 + tasa, n);
      df += (-n * cuota) / Math.pow(1 + tasa, n + 1);
    }

    f -= montoPrestamo;

    // Actualización de la tasa usando Newton-Raphson
    const nuevaTasa = tasa - f / df;

    if (Math.abs(nuevaTasa - tasa) < tolerance) {
      tasa = nuevaTasa; // Convergencia encontrada
      break;
    }

    tasa = nuevaTasa;
  }

  const finalRate = ((1 + tasa) ** 12 - 1) * 100;

  return finalRate;
}

const Summary = (props: any) => {
  const { creditDetails, amortizationSchedule } = props;
  if (!creditDetails) return null;

  if (!amortizationSchedule.length) return null;

  const firstPayment = new Date(amortizationSchedule[0].paymentDate);
  const lastPayment = new Date(
    amortizationSchedule[amortizationSchedule.length - 1].paymentDate,
  );
  const totalPayments = amortizationSchedule.length;
  const paymentDay = firstPayment.getDate() + 1;
  const monthlyInterestRate = parseFloat(creditDetails.rate);
  const annualInterestRate =
    parseFloat(creditDetails.anualRate) || parseFloat(creditDetails.rate) * 12;
  const operationalExpenses = creditDetails.operationCost || 0;
  const operationalExpensesIVA = Math.round(creditDetails.operationCost * 0.19);
  const otherExpenses = creditDetails.otherExpenses || 0;
  const totalLoanAmount = creditDetails.amount;
  const taxes = creditDetails.taxes || 0;
  const notary = creditDetails.notary || 0;
  const guaranteeAmount = creditDetails.guaranteeAmount || 0;
  const interestType =
    creditDetails.interestType === 'COMPOUND' ? 'Compuesto' : 'Simple';
  const amountToDeposit =
    totalLoanAmount - guaranteeAmount - creditDetails.amountToApply;
  const totalCost = amortizationSchedule.reduce(
    (acc: number, curr: any) => acc + curr.amount,
    0,
  );
  const ratePrice = amortizationSchedule.reduce(
    (acc: number, curr: any) => acc + curr.interest,
    0,
  );

  const capital = amortizationSchedule.reduce(
    (acc: number, curr: any) => acc + curr.principal,
    0,
  );

  // Calculamos la TIR mensual
  const cae = calculateIRR(
    amortizationSchedule[0].amount,
    totalPayments,
    creditDetails.amount,
  );

  const InfoRow = ({
    label,
    value,
    bold = false,
  }: {
    label: string;
    value: string | number;
    bold?: boolean;
  }) => (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        py: 0.5,
        alignItems: 'center',
      }}
    >
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ fontWeight: bold ? 600 : 400 }}
        color={bold ? 'primary.main' : 'text.primary'}
      >
        {value}
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ width: '100%', mb: 3 }}>
      <Grid container spacing={3} alignItems="stretch">
        <Grid item xs={12} md={5}>
          <Paper
            elevation={2}
            sx={{
              height: '100%',
              py: 1,
              px: 3,
              borderRadius: 2,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                mb: 0.5,
                color: 'primary.main',
              }}
            >
              Datos del crédito
            </Typography>
            <Divider sx={{ mb: 1 }} />
            <Box>
              <InfoRow label="Primer pago" value={formatDate(firstPayment)} />
              <InfoRow label="Último pago" value={formatDate(lastPayment)} />
              <InfoRow label="Total de pagos" value={totalPayments} />
              <InfoRow label="Día de pago" value={String(paymentDay)} />
              <InfoRow label="Tipo de interés de pago" value={interestType} />
              <InfoRow
                label="Tasa de interés mensual"
                value={`${monthlyInterestRate.toFixed(2)}%`}
              />
              <InfoRow
                label="Tasa de interés anual"
                value={`${annualInterestRate.toFixed(2)}%`}
              />
              <InfoRow label="CAE" value={`${cae.toFixed(2)}%`} />
            </Box>
          </Paper>
        </Grid>

        {/* Desglose de costos */}
        <Grid item xs={12} md={7}>
          <Paper
            elevation={2}
            sx={{
              height: '100%',
              py: 1,
              px: 3,
              borderRadius: 2,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                mb: 0.5,
                color: 'primary.main',
              }}
            >
              Desglose de costos
            </Typography>
            <Divider sx={{ mb: 1 }} />
            <Grid container spacing={3}>
              {/* Columna izquierda */}
              <Grid item xs={12} sm={6}>
                <InfoRow
                  label="Comisión"
                  value={formatMoney(operationalExpenses)}
                />
                <InfoRow
                  label="Comisión IVA"
                  value={formatMoney(operationalExpensesIVA)}
                />
                <InfoRow
                  label="Gastos operacionales"
                  value={formatMoney(otherExpenses)}
                />
                <InfoRow label="Notaría" value={formatMoney(notary)} />
                <InfoRow label="Impuestos" value={formatMoney(taxes)} />

                <InfoRow
                  label="Monto total del crédito"
                  value={formatMoney(totalLoanAmount)}
                />
                <Divider sx={{ my: 0.5 }} />
                <InfoRow label="Capital" value={formatMoney(capital)} bold />
              </Grid>

              {/* Columna derecha */}
              <Grid item xs={12} sm={6}>
                <InfoRow label="Capital" value={formatMoney(capital)} />
                <InfoRow
                  label="Diferencia de precio"
                  value={formatMoney(ratePrice)}
                />
                <Divider sx={{ my: 0.5 }} />
                <InfoRow
                  label="Costo del crédito"
                  value={formatMoney(totalCost)}
                  bold
                />
                <Box sx={{ my: 3 }} />
                <InfoRow
                  label="Monto total del crédito"
                  value={formatMoney(totalLoanAmount)}
                />
                <InfoRow
                  label="Monto en garantía"
                  value={formatMoney(guaranteeAmount)}
                />
                <InfoRow
                  label="Documentos a aplicar"
                  value={formatMoney(creditDetails.amountToApply)}
                />
                <Divider sx={{ my: 0.5 }} />
                <InfoRow
                  label="Monto a depositar"
                  value={formatMoney(amountToDeposit)}
                  bold
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Summary;
